import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  CalendarIcon,
  CheckIcon,
  PencilIcon,
  PuzzlePieceIcon,
  ChevronUpDownIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';
import React, { Fragment, FunctionComponent } from 'react';
import { useBriefMutations } from '../../hooks/useBriefs';
import { SidebarTool } from '../../utils/SidebarTool';
import { Account } from '../../services/models/account';
import { Brief, getKeywordsByType } from '../../services/models/brief';
import { BriefKeywordType } from '../../services/models/brief-keyword-type';
import { BriefLanguage, supportedBriefLanguages } from '../../services/models/brief-language';
import { DayOfWeek } from '../../services/models/day-of-week';
import { classNames } from '../../utils/class-names';
import EditBriefKeyword from '../edit-brief-keyword/EditBriefKeyword';
import EditBriefRecipients from '../edit-brief-recipients/EditBriefRecipients';
import EditBriefSchedule from '../edit-brief-schedule/EditBriefSchedule';
import EditSlackIntegration from '../edit-slack-integration/EditSlackIntegration';

export interface EditOption {
  name: string;
  tool: SidebarTool;
  icon: any;
}

const EDIT_OPTIONS: EditOption[] = [
  { name: 'Edit', tool: SidebarTool.EDIT_BRIEF, icon: PencilIcon },
  { name: 'Schedule', tool: SidebarTool.EDIT_SCHEDULE, icon: CalendarIcon },
  { name: 'Recipients', tool: SidebarTool.EDIT_RECIPIENTS, icon: UsersIcon },
  { name: 'Integrations', tool: SidebarTool.EDIT_INTEGRATIONS, icon: PuzzlePieceIcon },
];

interface EditBriefPanelProps {
  account: Account;
  brief: Brief;
  tool: SidebarTool;
  panelOpen: boolean;
  onClosePanel: () => void;
}
const EditBriefPanel: FunctionComponent<EditBriefPanelProps> = ({
  account,
  brief,
  tool,
  panelOpen,
  onClosePanel,
}) => {
  const editOption = EDIT_OPTIONS.find(eo => eo.tool === tool);

  if (editOption === null) {
    return null;
  }

  return (
    <Transition show={panelOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-hidden" onClose={onClosePanel}>
        <div className="absolute inset-0 overflow-hidden">
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="absolute inset-0 bg-gray-500/75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="w-screen max-w-md">
                <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-center justify-between pb-4 border-b border-primary">
                      <DialogTitle className="text-2xl font-bold text-primary m-0">
                        <span className="group flex items-center">
                          {editOption !== undefined && (
                            <>
                              <editOption.icon className="mr-3 shrink-0 h-6 w-6" />{' '}
                              {editOption.name}
                            </>
                          )}
                        </span>
                      </DialogTitle>
                      <div className="ml-3 h-7 flex items-center">
                        <button
                          type="button"
                          className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          onClick={() => onClosePanel()}
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="relative flex-1 px-4 overflow-y-auto sm:px-6">
                    <EditBrief account={account} brief={brief} tool={tool} />
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

interface EditBriefProps {
  account: Account;
  brief: Brief;
  tool: SidebarTool;
}

const EditBrief: React.FC<EditBriefProps> = ({ account, brief: briefProp, tool }) => {
  const {
    updateLanguage,
    createBriefKeyword,
    destroyBriefKeyword,
    updateSchedule,
    createRecipient,
    destroyRecipient,
    removeSlackChannel,
    isUpdatingKeywords,
    isUpdatingSchedule,
    isUpdatingRecipients,
    isUpdatingSlackChannels,
  } = useBriefMutations();

  // The edit tools read their busy flags off the brief object (the retired
  // reducers stored them there); keep that contract with a local overlay.
  const brief: Brief = {
    ...briefProp,
    isUpdatingKeywords,
    isUpdatingSchedule,
    isUpdatingRecipients,
    isUpdatingSlackChannels,
  };

  const productFeatures = account.product.features;
  const selectLanguage =
    supportedBriefLanguages.find(sl => sl.lang === brief.language) || supportedBriefLanguages[0];

  // Brief Keywords
  const briefTopics = getKeywordsByType(brief, BriefKeywordType.Topic);
  const briefFilters = getKeywordsByType(brief, BriefKeywordType.Filter);
  const briefExclusions = getKeywordsByType(brief, BriefKeywordType.Exclusion);

  const handleLanguageChange = (lang: BriefLanguage) => {
    if (Object.values(BriefLanguage).includes(lang)) {
      updateLanguage(brief.id, lang);
    }
  };

  const handleTopicAddition = (brief: Brief, keyword: string) => {
    const matchingExclusion = (briefExclusions || []).find(
      x => x.keyword?.toLowerCase() === keyword.toLocaleLowerCase()
    );

    if (matchingExclusion && matchingExclusion.id) {
      destroyBriefKeyword(brief.id, matchingExclusion.id);
    }

    createBriefKeyword(brief.id, keyword, BriefKeywordType.Topic);
  };

  const briefTool = () => (
    <div className="w-full">
      <div className="mt-6">
        <div className="mb-4">
          <div className="block text-xl font-bold text-primary mb-2">Language</div>
          <div className="mb-4 block text-sm text-gray-400">
            Show articles written in the given language
          </div>
          <Listbox value={brief.language} onChange={handleLanguageChange}>
            <div className="mt-1 relative">
              <ListboxButton className="bg-white relative w-full border border-gray-300 rounded-md shadow-xs pl-3 pr-10 py-2 text-left cursor-default focus:outline-hidden focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                <span className="block truncate">{selectLanguage.label}</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </ListboxButton>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm">
                  {supportedBriefLanguages.map(item => (
                    <ListboxOption
                      key={item.lang}
                      className={({ focus }) =>
                        classNames(
                          focus ? 'text-white bg-primary' : 'text-gray-900',
                          'cursor-default select-none relative py-2 pl-3 pr-9'
                        )
                      }
                      value={item.lang}
                    >
                      {({ selected, focus }) => (
                        <>
                          <span
                            className={classNames(
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate'
                            )}
                          >
                            {item.label}
                          </span>

                          {selected ? (
                            <span
                              className={classNames(
                                focus ? 'text-white' : 'text-primary',
                                'absolute inset-y-0 right-0 flex items-center pr-4'
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </Listbox>
        </div>

        {/* Topic Keywords */}
        <EditBriefKeyword
          title="Topics"
          maxItems={productFeatures.keywordsPerBrief}
          placeholder="press ENTER after each keyword"
          keywords={briefTopics}
          isUpdating={!!brief.isUpdatingKeywords}
          helpSummary={<span>Enter related keywords on the topics you are interested</span>}
          helpContent={
            <>
              <p>
                Enter any keyword to start your News Brief. Keep your News Brief focused by using
                related terms, ie: machine learning, AI.{' '}
              </p>
              <p>
                Press <code>ENTER</code> after each one.
              </p>
            </>
          }
          create={keyword => handleTopicAddition(brief, keyword)}
          destroy={keywordId => destroyBriefKeyword(brief.id, keywordId)}
          validate={keyword =>
            (briefExclusions || []).some(x => x.keyword === keyword)
              ? `There's an exclusion with this keyword`
              : null
          }
        />

        {/* Filter Keywords */}
        <EditBriefKeyword
          title="Filters"
          maxItems={productFeatures.keywordsPerBrief}
          placeholder="press ENTER after each keyword"
          keywords={briefFilters}
          isUpdating={!!brief.isUpdatingKeywords}
          helpSummary={
            <span>
              Articles <strong>must</strong> include at least one of these words
            </span>
          }
          helpContent={
            <>
              <p>
                Only articles that have one of these keywords <strong>AND</strong> one of your
                topics will appear. ie: trends, updates, tools.
              </p>
              <p>
                Press <code>ENTER</code> after each word.
              </p>
            </>
          }
          optional={true}
          create={keyword => createBriefKeyword(brief.id, keyword, BriefKeywordType.Filter)}
          destroy={keywordId => destroyBriefKeyword(brief.id, keywordId)}
        />

        <EditBriefKeyword
          title="Exclusions"
          maxItems={productFeatures.keywordsPerBrief}
          placeholder="press ENTER after each keyword"
          keywords={briefExclusions}
          isUpdating={!!brief.isUpdatingKeywords}
          helpSummary={
            <span>
              Articles must <strong>not</strong> include any of these words
            </span>
          }
          helpContent={
            <>
              <p>Any article containing these words won't appear. ie: jobs, courses, webinars.</p>
              <p>
                Press <code>ENTER</code> after each word.
              </p>
            </>
          }
          optional={true}
          create={keyword => createBriefKeyword(brief.id, keyword, BriefKeywordType.Exclusion)}
          destroy={keywordId => destroyBriefKeyword(brief.id, keywordId)}
          validate={keyword =>
            (briefTopics || []).some(x => x.keyword === keyword)
              ? `There's a topic with this keyword`
              : null
          }
        />
      </div>
    </div>
  );

  const recipientsTool = () => (
    <div className="w-full">
      <div className="mt-6">
        <EditBriefRecipients
          brief={brief}
          create={email => createRecipient(brief.id, email)}
          destroy={(recipientId: number) => destroyRecipient(brief.id, recipientId)}
          maxRecipients={account.product.features.recipientsPerBrief}
        />
      </div>
    </div>
  );

  const scheduleTool = () => (
    <div className="w-full">
      <div className="mt-6">
        <EditBriefSchedule
          brief={brief}
          update={(hour: number, days: DayOfWeek[]) => updateSchedule(brief.id, hour, days)}
        />
      </div>
    </div>
  );

  const slackTool = () => (
    <div className="w-full">
      <div className="mt-6">
        <EditSlackIntegration
          brief={brief}
          destroy={(slackChannelId: number) => removeSlackChannel(brief.id, slackChannelId)}
        />
      </div>
    </div>
  );

  return (
    <div className="absolute inset-0 px-4 sm:px-6">
      {tool === SidebarTool.EDIT_BRIEF && briefTool()}
      {tool === SidebarTool.EDIT_RECIPIENTS && recipientsTool()}
      {tool === SidebarTool.EDIT_SCHEDULE && scheduleTool()}
      {tool === SidebarTool.EDIT_INTEGRATIONS && slackTool()}
    </div>
  );
};

export default EditBriefPanel;
